import './styles.css';
import { project } from './calc/pension';
import { validate, hasErrors } from './calc/validate';
import { DEFAULT_INPUTS } from './calc/presets';
import type { Inputs } from './calc/types';
import { decodeInputs, encodeInputs, loadInputs, saveInputs } from './state/url';
import { initForm, readForm, showErrors, syncCustomVisibility, updateCurrencySymbols, updateStageRanges, writeForm } from './ui/form';
import { renderEmpty, renderInvalid, renderResults } from './ui/results';
import { initCountryCount, initCountryPicker, initMethodology, renderCountries, type CountryElements } from './ui/countries';

const form = must(document.querySelector<HTMLFormElement>('#calc-form'), '#calc-form');
const results = must(document.querySelector<HTMLElement>('#results'), '#results');
const countryElements: CountryElements = {
  picker: must(document.querySelector<HTMLSelectElement>('#country-picker'), '#country-picker'),
  detail: must(document.querySelector<HTMLElement>('#country-detail'), '#country-detail'),
  recommendations: must(document.querySelector<HTMLElement>('#country-recommendations'), '#country-recommendations'),
  tiers: must(document.querySelector<HTMLElement>('#country-tiers'), '#country-tiers'),
  methodology: must(document.querySelector<HTMLElement>('#country-methodology'), '#country-methodology'),
  fullTable: must(document.querySelector<HTMLElement>('#country-full-table'), '#country-full-table'),
};

/** Set while we write the hash ourselves, so our own change doesn't bounce back. */
let writingHash = false;
let lastInputs: Inputs = DEFAULT_INPUTS;

initForm(form);
initCountryCount();
initCountryPicker(countryElements.picker);
initMethodology(countryElements.methodology);
countryElements.picker.addEventListener('change', () => render(lastInputs));
// A link beats a remembered session: a shared URL must show what the sender saw.
writeForm(form, location.hash.length > 1 ? decodeInputs(location.hash) : loadInputs() ?? DEFAULT_INPUTS);
update();

form.addEventListener('input', debounce(update, 150));
form.addEventListener('change', () => {
  syncCustomVisibility(form);
  update();
});

window.addEventListener('hashchange', () => {
  if (writingHash) {
    writingHash = false;
    return;
  }
  writeForm(form, decodeInputs(location.hash));
  update();
});

// Re-render on resize: the chart is drawn at real pixel width, not scaled.
window.addEventListener('resize', debounce(() => render(lastInputs), 150));

document.querySelector('#reset')?.addEventListener('click', () => {
  writeForm(form, DEFAULT_INPUTS);
  update();
});

document.querySelector('#copy-link')?.addEventListener('click', async (event) => {
  const button = event.currentTarget as HTMLButtonElement;
  const original = button.dataset.label ?? button.textContent ?? '';
  button.dataset.label = original;
  try {
    await navigator.clipboard.writeText(location.href);
    button.textContent = 'Link copied';
  } catch {
    button.textContent = 'Press ⌘C to copy the address bar';
  }
  setTimeout(() => {
    button.textContent = original;
  }, 2000);
});

function update(): void {
  const input = readForm(form);
  lastInputs = input;
  updateCurrencySymbols(form, input.currency);
  updateStageRanges(form, input);

  const errors = validate(input);
  showErrors(form, errors);

  const hash = `#${encodeInputs(input)}`;
  if (hash !== location.hash) {
    writingHash = true;
    history.replaceState(null, '', hash);
    writingHash = false;
  }
  saveInputs(input);

  render(input, hasErrors(errors));
}

function render(input: Inputs, invalid = hasErrors(validate(input))): void {
  const empty = input.currentPot === 0 && input.stages.every((s) => s.annualContribution === 0);

  if (invalid) {
    renderInvalid(results);
  } else if (empty) {
    renderEmpty(results);
  } else {
    renderResults(results, input, project(input));
  }

  void renderCountries(countryElements, input, invalid || empty ? null : project(input));
}

function must<T>(node: T | null, selector: string): T {
  if (!node) throw new Error(`Calculator markup is missing ${selector}`);
  return node;
}

function debounce<T extends (...args: never[]) => void>(fn: T, ms: number): T {
  let timer: number | undefined;
  return ((...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms) as unknown as number;
  }) as T;
}
